//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TrashCan, WarningAlt } from '@carbon/icons-react';
import defaultAvatar from '../../../icons/DefaultAvatar.svg';

import { useUserAuth } from '../../../UserAuth';
import { useConfig } from '../../../Config';

interface SelectedMemberProps {
  username: string;
  fullname?: string;
  onRemove: (username: string) => void;
  isAdding: boolean;
  setSearchQuery?: (username: string) => void;
  isInvite: boolean;
  mode?: 'loop' | 'teamspace';
  memberOfLoops?: string[];
  isNewlyAdded?: boolean;
}

/**
 * Displays a single member in a list for editing or adding.
 *
 * @param {object} props The component props.
 * @param {string} props.username The member's username or email.
 * @param {string} [props.fullname] The member's full name.
 * @param {function} props.onRemove Callback to handle removing the member.
 * @param {boolean} props.isAdding If true, component is in add mode.
 * @param {function} [props.setSearchQuery] Callback to set the search query on click.
 * @param {boolean} props.isInvite If true, displays the user as a new invitee.
 * @returns {JSX.Element} The rendered EditMembers component.
 */
const EditMembers: React.FC<SelectedMemberProps> = ({
  username,
  fullname,
  onRemove,
  isAdding,
  setSearchQuery,
  isInvite,
  mode,
  memberOfLoops,
  isNewlyAdded,
}) => {
  const { t } = useTranslation();
  const currentUser = useUserAuth().user?.profile?.preferred_username || '';
  const isCurrentUser = currentUser === username;
  const { services } = useConfig();
  const canDeleteUser = services?.delete_user?.show === true;

  const isMemberOfLoopsInTeamspace =
    mode === 'teamspace' && memberOfLoops && memberOfLoops.length > 0;
  const isRemovalDisabled = isCurrentUser || isMemberOfLoopsInTeamspace;
  const removalTooltip = isCurrentUser
    ? t('remove-self')
    : isMemberOfLoopsInTeamspace
      ? t('remove-member-with-loops', {
          loops: memberOfLoops.join(', '),
        })
      : t('remove-member');

  const handleClick = useCallback(() => {
    if (isAdding && setSearchQuery) {
      setSearchQuery(username);
    } else if (!isCurrentUser) {
      onRemove(username);
    }
  }, [isAdding, setSearchQuery, username, onRemove, isCurrentUser]);

  const getInitials = (name?: string) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  return (
    <div
      id={`edit-member-${username}`}
      data-testid={`edit-member-container-${username}`}
      role="button"
      tabIndex={isAdding ? 0 : -1}
      className={`flex items-center gap-3 border-b border-border py-3 transition-colors hover:bg-secondary ${
        isAdding ? 'cursor-pointer' : 'cursor-default'
      }`}
      onClick={isAdding ? handleClick : undefined}
      onKeyDown={
        isAdding
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') handleClick();
            }
          : undefined
      }
    >
      <div
        id={`edit-member-details-${username}`}
        data-testid={`edit-member-details-${username}`}
        className="flex min-w-0 flex-1 items-center gap-3"
      >
        {/* Avatar with initials */}
        <div
          id={`edit-member-avatar-${username}`}
          data-testid={`edit-member-avatar-${username}`}
          className="relative flex size-11 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground"
        >
          <img
            src={defaultAvatar}
            alt={t('Profile')}
            className="absolute inset-0 size-full rounded-full opacity-0"
          />
          {getInitials(fullname)}
        </div>

        <div
          id={`edit-member-text-content-${username}`}
          data-testid={`edit-member-text-content-${username}`}
          className="flex min-w-0 flex-1 flex-col items-start"
        >
          {isInvite ? (
            <>
              <div
                id="id-edit-member-invite-name-row"
                data-testid="edit-member-invite-name-row"
                className="flex items-center gap-2"
              >
                <span
                  id={`edit-member-name-${fullname}`}
                  data-testid={`edit-member-invite-name-${fullname}`}
                  className="break-all text-sm font-medium text-foreground"
                >
                  {fullname}
                </span>
                {isNewlyAdded && (
                  <span
                    id="id-edit-member-invite-new-badge"
                    data-testid="edit-member-invite-new-badge"
                    className="rounded bg-alert px-1.5 py-0.5 text-[10px] font-semibold uppercase text-alert-foreground"
                  >
                    {t('new-badge', 'NEW')}
                  </span>
                )}
              </div>
              <span
                id="id-edit-member-invite-warning"
                data-testid="edit-member-invite-warning"
                className="mt-1 flex items-center text-xs text-secondary-foreground"
              >
                <WarningAlt size={16} className="mr-1.5 text-current" />
                {t('not-teamspace-member')}
              </span>
            </>
          ) : (
            <>
              <div
                id="id-edit-member-name-row"
                data-testid="edit-member-name-row"
                className="flex items-center gap-2"
              >
                <span
                  id={`edit-member-name-${fullname}`}
                  data-testid={`edit-member-name-${fullname}`}
                  className="truncate break-all text-sm font-medium text-foreground"
                >
                  {fullname}
                </span>
                {isNewlyAdded && (
                  <span
                    id="id-edit-member-new-badge"
                    data-testid="edit-member-new-badge"
                    className="rounded bg-alert px-1.5 py-0.5 text-[10px] font-semibold uppercase text-alert-foreground"
                  >
                    {t('new-badge', 'NEW')}
                  </span>
                )}
              </div>
              <span
                id={`edit-member-email-${username}`}
                data-testid={`edit-member-email-${username}`}
                className="truncate break-all text-xs text-muted-foreground"
              >
                {username}
              </span>
            </>
          )}
          {isCurrentUser && !isAdding && (
            <span
              id="id-edit-member-current-user-label"
              data-testid="edit-member-current-user-label"
              className="text-xs text-muted-foreground"
            ></span>
          )}
        </div>
      </div>
      {!isAdding && canDeleteUser && (
        <button
          id={`edit-member-remove-button-${username}`}
          data-testid={`edit-member-remove-button-${username}`}
          type="button"
          onClick={isRemovalDisabled ? undefined : handleClick}
          disabled={isRemovalDisabled}
          className={`shrink-0 rounded p-1 transition-colors ${
            isRemovalDisabled
              ? 'cursor-not-allowed opacity-40'
              : 'cursor-pointer hover:bg-destructive/10'
          }`}
          title={removalTooltip}
          aria-label={removalTooltip}
        >
          <TrashCan size={20} className="text-destructive" />
        </button>
      )}
    </div>
  );
};

export default EditMembers;
